import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import CommandLayout from "../../components/war-room/CommandLayout";
import CorpusSearch from "../../components/war-room/CorpusSearch";
import ThreeWayReview, { reviewColor, type ReviewOption } from "../../components/war-room/ThreeWayReview";
import { useCaseJustification, useSetJustificationAssessment, type JustificationClaim, type JustificationElement } from "../../hooks/useCaseJustification";

/**
 * Layer 4 — Justification ledger. For conduct the authority layer did NOT clear
 * (unauthorized / contested), does an affirmative defense EXCUSE it? The engine
 * renders a verdict (excused / not excused / unclear / n-a); the human makes one
 * 3-way call — Excused / Partly / Not Excused. Detail (cited defenses, the
 * two-model read, corpus search, note) lives behind "details".
 */
const JUSTIFICATION_OPTIONS: ReviewOption[] = [
  { key: "excused", label: "Excused", color: "#5a8f6b", mark: "✓" },
  { key: "partial", label: "Partly Excused", color: "#c9a23a", mark: "◐" },
  { key: "not_excused", label: "Not Excused", color: "#b3503f", mark: "✕" },
];
const UNREVIEWED = "#a78bdb";

function JustificationLedger() {
  const { caseId } = useParams<{ caseId: string }>();
  const { data: claims, isLoading, error } = useCaseJustification(caseId);

  let total = 0, excused = 0, notExcused = 0, reviewed = 0, pending = 0;
  for (const c of claims ?? []) for (const el of c.elements) {
    total++;
    if (el.verdict === "excused") excused++;
    if (el.verdict === "not_excused") notExcused++;
    if (el.human_assessment) reviewed++;
    if (el.needs_review) pending++;
  }

  return (
    <CommandLayout caseId={caseId} variant="ctafinal">
      <header className="rounded border border-steel-dark bg-iron/70 px-6 py-6 backdrop-blur-sm">
        <p className="mb-2 font-display text-base font-light uppercase tracking-[0.18em] text-bone-mute">// layer 4 — justification</p>
        <h2 className="font-display text-2xl font-light leading-tight tracking-tight text-bone md:text-3xl"
            style={{ textShadow: "0 2px 16px rgba(0,0,0,0.6)" }}>
          Even if unauthorized, is it excused?
        </h2>
        <p className="mt-3 max-w-3xl font-display text-sm leading-relaxed text-bone-dim">
          Only conduct the authority layer left unauthorized or contested reaches here. For each, the engine asks whether an
          affirmative defense excuses it: <span style={{ color: vColor("excused") }}>excused</span> (a defense applies),
          <span style={{ color: vColor("unclear") }}> contested excuse</span> (a real defense that turns on a disputed fact —
          fought at summary judgment), or <span style={{ color: vColor("not_excused") }}> not excused</span> (no defense reaches it).
          You make the call:
          <span style={{ color: reviewColor("excused", JUSTIFICATION_OPTIONS) }}> Excused</span> /
          <span style={{ color: reviewColor("partial", JUSTIFICATION_OPTIONS) }}> Partly</span> /
          <span style={{ color: reviewColor("not_excused", JUSTIFICATION_OPTIONS) }}> Not Excused</span>.
        </p>

        {(claims?.length ?? 0) > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-3 font-display text-base font-light uppercase tracking-[0.1em] text-bone-mute">
            <Tally label="excused" n={excused} color={vColor("excused")} />
            <Tally label="not excused" n={notExcused} color={vColor("not_excused")} />
            <span>· {reviewed}/{total} reviewed</span>
            <span className="flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: UNREVIEWED }} />{pending} to review</span>
            <GradingGuide />
            <Link to={`/case/${caseId}/authority`} className="ml-auto normal-case tracking-normal text-blue-bright/80 hover:text-blue-bright">← authority</Link>
          </div>
        )}
      </header>

      <div className="space-y-3">
        {isLoading && <p className="font-display text-base text-bone-mute">// weighing the defenses…</p>}
        {error && <p className="font-display text-base text-ember-bright">// {error.message}</p>}
        {!isLoading && !error && (claims?.length ?? 0) === 0 && (
          <p className="rounded border border-steel-dark bg-iron/70 px-5 py-6 font-display text-base text-bone-mute">
            // no justification map yet — run the Layer 4 engine (justification_runner.py) first
          </p>
        )}
        {(claims ?? []).map((c) => <ClaimCard key={c.claim_id} c={c} />)}
      </div>
    </CommandLayout>
  );
}

const VC: Record<string, string> = { excused: "#5a8f6b", not_excused: "#b3503f", unclear: "#c9a23a", not_applicable: "#6b7480", error: "#6b7480" };
const vColor = (k: string | null | undefined): string => (k && VC[k]) || "#5a6470";
const VL: Record<string, string> = { excused: "excused", not_excused: "not excused", unclear: "contested excuse", not_applicable: "n/a", error: "error" };
const vLabel = (k: string | null | undefined): string => (k && (VL[k] ?? k)) || "—";
const STAGE_L: Record<string, string> = { facial: "facial excuse", merits_sj: "contested · summary judgment", na: "" };
const GUIDE: Record<string, string> = {
  excused: "An affirmative defense squarely covers the act — the element fails even though it was unauthorized.",
  unclear: "A real defense applies but turns on a disputed fact (good faith, reasonable reliance, disinterestedness) — fought at summary judgment.",
  not_excused: "No defense on record reaches the act — it survives to be litigated on the merits.",
  not_applicable: "Not the kind of element a justification addresses (duty, causation, damages, relief).",
};

function Tally({ label, n, color }: { label: string; n: number; color: string }) {
  return <span className="flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />{n} {label}</span>;
}

function GradingGuide() {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative normal-case tracking-normal">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 rounded border border-steel-dark/70 px-2 py-0.5 text-bone-mute hover:text-bone">ⓘ what the verdicts mean</button>
      {open && (
        <div className="absolute left-0 z-20 mt-1 w-[24rem] rounded border border-steel-dark bg-charcoal/95 p-3 shadow-lg backdrop-blur-sm">
          {(["excused", "unclear", "not_excused", "not_applicable"] as const).map((k) => (
            <div key={k} className="mb-2 flex items-start gap-2">
              <span className="mt-0.5 shrink-0 rounded px-2 py-0.5 font-display text-sm uppercase tracking-[0.1em]" style={{ background: vColor(k), color: "#0c0f12" }}>{vLabel(k)}</span>
              <span className="font-display text-[12px] leading-snug text-bone-dim">{GUIDE[k]}</span>
            </div>
          ))}
          <p className="font-display text-[11px] italic text-bone-mute/50">Analytical guidance — your counsel makes the final call.</p>
        </div>
      )}
    </span>
  );
}

function ClaimCard({ c }: { c: JustificationClaim }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded border border-steel-dark bg-iron/70 backdrop-blur-sm">
      <button type="button" onClick={() => setOpen((o) => !o)} className="block w-full text-left hover:bg-iron-2/40">
        <div className="flex items-start gap-3 px-5 py-3">
          <div className="min-w-0 flex-1">
            <p className="font-display text-base font-light text-bone">{c.claim_name}</p>
            {c.target && <p className="mt-0.5 font-display text-sm text-bone-mute/70">vs {c.target}</p>}
          </div>
          <span className="shrink-0 rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.1em]"
                style={{ background: c.excused > 0 ? vColor("excused") : (c.not_excused > 0 ? vColor("not_excused") : vColor("unclear")), color: "#0c0f12" }}>
            {c.excused} excused · {c.unclear} contested · {c.not_excused} not · {c.reviewed}/{c.total} reviewed
          </span>
          <span className="mt-0.5 shrink-0 font-display text-base text-bone-mute">{open ? "▾" : "▸"}</span>
        </div>
      </button>
      {open && (
        <div className="border-t border-steel-dark/60 px-5 py-3 space-y-2">
          {c.elements.map((el) => <ElementRow key={el.id} el={el} />)}
        </div>
      )}
    </div>
  );
}

function ElementRow({ el }: { el: JustificationElement }) {
  const { caseId } = useParams<{ caseId: string }>();
  const set = useSetJustificationAssessment(caseId);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(el.human_note ?? "");
  const split = el.review_status === "split";
  const stage = el.stage && STAGE_L[el.stage] ? STAGE_L[el.stage] : "";
  const border = el.human_assessment ? reviewColor(el.human_assessment, JUSTIFICATION_OPTIONS) : (el.needs_review ? UNREVIEWED : vColor(el.verdict));
  const choose = (v: string) => set.mutate({ id: el.id, assessment: v, note: note.trim() || undefined });

  return (
    <div className="rounded border border-steel-dark/60 bg-black/20">
      <div className="border-l-2 px-3 py-2" style={{ borderColor: border }}>
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-start gap-2 text-left">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="rounded px-2 py-0.5 font-display text-[11px] font-light uppercase tracking-[0.1em]" style={{ background: vColor(el.verdict), color: "#0c0f12" }}>{vLabel(el.verdict)}</span>
              {stage && <span className="font-display text-[11px] font-light uppercase tracking-[0.08em] text-bone-mute/70">{stage}</span>}
              {split && <span className="rounded px-1.5 py-0.5 font-display text-[11px] uppercase tracking-[0.1em]" style={{ background: UNREVIEWED, color: "#0c0f12" }}>split</span>}
              {el.needs_review && <span className="font-display text-[11px] uppercase tracking-[0.1em]" style={{ color: UNREVIEWED }}>· needs your call</span>}
            </div>
            <p className="font-display text-[13px] text-bone">{el.element_name}</p>
          </div>
          <span className="mt-0.5 shrink-0 font-display text-sm text-bone-mute">{open ? "▾ details" : "▸ details"}</span>
        </button>

        <div className="mt-2.5">
          <ThreeWayReview value={el.human_assessment} options={JUSTIFICATION_OPTIONS} disabled={set.isPending} onChange={choose} />
          {set.isError && <p className="mt-1 font-display text-sm text-ember-bright">// {(set.error as Error).message}</p>}
        </div>
      </div>

      {open && (
        <div className="border-l-2 px-3 pb-3" style={{ borderColor: border }}>
          {el.rationale && <p className="mt-2 font-display text-[12px] leading-snug text-bone-dim">{el.rationale}</p>}

          {(el.cited?.length ?? 0) > 0 && (
            <div className="mt-2">
              <p className="mb-1 font-display text-[11px] font-light uppercase tracking-[0.12em] text-bone-mute/70">// defenses relied on</p>
              <div className="flex flex-wrap gap-1.5">
                {(el.cited ?? []).map((d) => (
                  <span key={d.defense_id} className="rounded border border-steel-dark/70 px-2 py-0.5 font-display text-[11px] text-bone-dim">{d.title}</span>
                ))}
              </div>
            </div>
          )}

          {(el.proposer_verdict || el.judge_verdict) && (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-display text-[11px] font-light uppercase tracking-[0.08em] text-bone-mute/70">
              <span className="flex items-center gap-1">proposer<span className="rounded px-1 py-0.5" style={{ background: vColor(el.proposer_verdict), color: "#0c0f12" }}>{vLabel(el.proposer_verdict)}</span></span>
              <span className="flex items-center gap-1">cross-exam<span className="rounded px-1 py-0.5" style={{ background: vColor(el.judge_verdict), color: "#0c0f12" }}>{vLabel(el.judge_verdict)}</span></span>
              {split && <span className="text-bone-mute/60">— they disagreed; the cross-examiner's call stands</span>}
            </div>
          )}

          <textarea value={note} onChange={(e) => setNote(e.target.value)}
            onBlur={() => { if ((note.trim() || null) !== (el.human_note ?? null) && el.human_assessment) choose(el.human_assessment); }} rows={1}
            placeholder="note — why (saved with your call)"
            className="mt-2 w-full resize-y rounded border border-steel-dark/60 bg-black/30 px-2 py-1 font-display text-[12px] text-bone placeholder:text-bone-mute/50" />

          <CorpusSearch seed={el.element_name ?? ""} label="⌕ search the corpus for this" />
        </div>
      )}
    </div>
  );
}

export default JustificationLedger;
